<script>
/* Imports */
import { mapActions, mapGetters } from 'vuex';
import { useProductOptions } from '@garmin/ui-product-ems-filters';

/* Components */
import VatTooltip from '../components/VatTooltip.vue';
import Titles from '../components/Titles.vue';
import ProductAvailability from '../components/ProductAvailability.vue';
import ProductFootnote from '../components/ProductFootnote.vue';
import Kicker from '../components/Kicker.vue';
import ProductInfoPrice from '../components/ProductInfoPrice.vue';
import KlarnaWrapper from '../components/KlarnaWrapper.vue';
import KlarnaWidget from '../components/KlarnaWidget.vue';
import CtasWrapper from '../components/CtasWrapper.vue';
import Ems from '../components/Ems.vue';
import AffirmWidget from '../components/AffirmWidget.vue';
import ShippingDisclaimer from '../components/ShippingDisclaimer.vue';
import ApacWidget from '../components/ApacWidget.vue';
import ProductInfoSkeletonLoader from '../components/ProductInfoSkeletonLoader.vue';
import TruemedWidget from '../components/TruemedWidget.vue';
import Breadcrumbs from '../components/Breadcrumbs.vue';

/* Composables */
import { useScreenResize } from '../composables/useScreenResize.js';

/* Helpers */
import {
  sanitizeAnalyticsString,
} from '../helpers/ga/analytics.js';
import { breakpoints } from '../helpers/breakpoints.js';

export default {
  name: 'ProductInfo',
  components: {
    Titles,
    Kicker,
    ProductAvailability,
    ProductInfoPrice,
    KlarnaWrapper,
    KlarnaWidget,
    CtasWrapper,
    ProductFootnote,
    Ems,
    VatTooltip,
    AffirmWidget,
    ApacWidget,
    ShippingDisclaimer,
    ProductInfoSkeletonLoader,
    TruemedWidget,
    Breadcrumbs,
  },
  setup() {
    const productOptions = useProductOptions();
    const { screenWidth } = useScreenResize();
    return {
      screenWidth,
      productOptions,
    };
  },
  computed: {
    ...mapGetters({
      productName: 'getProductName',
      simpleDisplayName: 'getSimpleDisplayName',
      showProductName: 'getShowProductName',
      productVariation: 'getProductVariation',
      subheadDisplayName: 'getSubheadDisplayName',
      showProductVariation: 'getShowProductVariationName',
      partNumber: 'getPartNumber',
      showPartNumber: 'getShowPartNumber',
      showKicker: 'getShowKicker',
      kickerText: 'getKickerText',
      kickerTextNoStyling: 'getKickerTextNoStyling',
      kickerIsDiscontinued: 'getKickerIsDiscontinued',
      priceData: 'getPriceData',
      formattedPrice: 'getFormattedPrice',
      showPriceData: 'getShowPriceData',
      showPriceLoader: 'getShowPriceLoader',
      displayedSalePrice: 'getDisplayedSalePrice',
      displayedSavings: 'getDisplayedSavings',
      shippingDisclaimer: 'getShippingDisclaimer',
      showShippingDisclaimer: 'getShowShippingDisclaimer',
      addToCartLoading: 'getAddToCartLoading',
      showBuyAction: 'getHasBuyAction',
      addToCartHref: 'getBuyActionHref',
      addToCartText: 'getBuyActionText',
      showDealerAction: 'getHasDealerAction',
      dealerActionHref: 'getDealerActionHref',
      dealerActionText: 'getDealerActionText',
      expressWarning: 'getExpressWarning',
      showExpressWarning: 'getShowExpressWarning',
      productHeaderDetails: 'getProductHeaderDetails',
      showProductHeaderDetails: 'getShowProductHeaderDetails',
      productAvailability: 'getProductAvailability',
      showProductAvailability: 'getShowProductAvailability',
      vatToolTip: 'getVatToolTip',
      showVatToolTip: 'getShowVatToolTip',
      productLegalText: 'getProductLegalText',
      showProductLegalText: 'getShowProductLegalText',
      productMarketingSummary: 'getProductMarketingSummary',
      showProductMarketingSummary: 'getShowProductMarketingSummary',
      showAddToCartBtn: 'getShowAddToCartBtn',
      widgetMetadata: 'getWidgetMetadata',
      showAffirmWidget: 'getShowAffirmWidget',
      showKlarnaWidget: 'getShowKlarnaWidget',
      showAPACWidget: 'getShowAPACWidget',
      galleryLoaded: 'getGalleryLoaded',
      loadingPrice: 'getLoadingPrice',
      kickersLoaded: 'getKickersLoaded',
      truemedEligible: 'getTruemedEligible',
      optionsList: 'getOptionsList',
      breadcrumbs: 'getBreadCrumbs',
      seoAttributes: 'getSeoAttributes',
    }),
    computedOptionsList() {
      if (window?.envSettings?.ITFE_PRODUCT_PAGE_ENABLE_REUSABLE_EMS_UI) {
        return this.productOptions.optionsList.value ?? [];
      }
      return this.optionsList ?? [];
    },
    showSubtitle() {
      // If this product has a productVariation and either color swatches or model images, show the SubTitle only if the subheadDisplayName is populated.
      if (this.productVariation && (this.containsColorSwatches || this.containsModelImages)) {
        return this.subheadDisplayName;
      }
      return this.showProductVariation;
    },
    containsColorSwatches() {
      const containsSwatches = this.computedOptionsList.some((option) => /^pvemsColorSwatch_.+$/i.test(option.id));
      return containsSwatches;
    },
    containsModelImages() {
      const containsModelImages = this.computedOptionsList.some((option) => option.type === 'image');
      return containsModelImages;
    },
    currencyCode() {
      return window?.utag_data?.currency_code;
    },
    getShowProductInfo() {
      return this.galleryLoaded && !this.loadingPrice && this.kickersLoaded;
    },
    getProductTitle() {
      return this?.simpleDisplayName || this?.productName || '';
    },
    getProductSubtitle() {
      return this?.subheadDisplayName || this?.productVariation || '';
    },
    productPrice() {
      return this.priceData?.salePrice?.price ?? this.priceData?.listPrice?.price ?? '';
    },
    discount() {
      return this.priceData?.savings?.price ?? '';
    },
    breadCrumbAnalyticsData() {
      return {
        productName: sanitizeAnalyticsString(this.seoAttributes?.productDisplayName),
        productPrice: this.productPrice,
        discount: this.discount,
        currency: this.currencyCode,
      };
    },
    isDesktopView() {
      return this.screenWidth >= breakpoints['desktop-s'];
    },
  },
  methods: {
    ...mapActions([
      'setAddToCartLoadingState',
      'setIsAffirmScriptLoaded',
    ]),
  },
};
</script>

<template>
  <div
    id="js__product__info"
    class="app__product__info"
  >
    <template v-if="getShowProductInfo">
      <Breadcrumbs
        v-if="isDesktopView"
        :crumbs="breadcrumbs"
        :analytics-data="breadCrumbAnalyticsData"
      />
      <Titles
        :title="getProductTitle"
        :show-product-name="showProductName"
        :subtitle="getProductSubtitle"
        :show-subtitle="showSubtitle"
        :part-number="partNumber"
        :show-part-number="showPartNumber"
      />
      <Kicker
        id="js__product__info__kicker"
        :show-kicker="showKicker"
        :kicker-text="kickerText"
        :kicker-text-no-styling="kickerTextNoStyling"
        :kicker-is-discontinued="kickerIsDiscontinued"
      />
      <ProductAvailability
        v-if="showProductAvailability"
        :content="productAvailability"
      />
      <ProductInfoPrice
        :formatted-price="formattedPrice"
        :show-price-loader="showPriceLoader"
        :show-price-data="showPriceData"
        :displayed-sale-price="displayedSalePrice"
        :displayed-savings="displayedSavings"
      />
      <KlarnaWrapper
        v-if="showKlarnaWidget"
        id="js__product__info__klarna"
      >
        <KlarnaWidget :widget-metadata="widgetMetadata" />
      </KlarnaWrapper>
      <AffirmWidget
        v-if="showAffirmWidget"
        id="js__product__info__affirm"
        :price-data="priceData"
        :set-is-affirm-script-loaded="setIsAffirmScriptLoaded"
      />
      <TruemedWidget
        v-if="truemedEligible"
        id="js__product__info__truemed"
      />
      <VatTooltip
        v-if="showVatToolTip"
        :content="vatToolTip"
      />
      <Ems />
      <CtasWrapper
        :show-add-to-cart-btn="showAddToCartBtn"
        :add-to-cart-text="addToCartText"
        :add-to-cart-href="addToCartHref"
        :show-dealer-btn="showDealerAction"
        :dealer-text="dealerActionText"
        :dealer-href="dealerActionHref"
        :express-warning="expressWarning"
        :show-express-warning="showExpressWarning"
      />
      <ShippingDisclaimer
        v-if="showShippingDisclaimer"
        :shipping-disclaimer-text="shippingDisclaimer"
      />
      <ApacWidget
        v-if="showAPACWidget"
        id="js__product__info__apac"
        :key="partNumber"
      />
      <ProductFootnote
        :product-header-details="productHeaderDetails"
        :show-product-header-details="showProductHeaderDetails"
        :product-legal-text="productLegalText"
        :show-product-legal-text="showProductLegalText"
        :product-marketing-summary="productMarketingSummary"
        :show-product-marketing-summary="showProductMarketingSummary"
        :part-number="partNumber"
      />
    </template>
    <template v-else>
      <ProductInfoSkeletonLoader />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.app__product {

  &__info {
    @extend %product-section;
    flex-basis: auto;
    display: block;
    max-width: 100%;
    text-align: left;
    margin-bottom: 1rem;
    padding-right: 0;

    @include productBreakpoint('desktop-s') {
      flex-basis: auto;
      max-width: 50vw;
    }

    @include productBreakpoint('desktop-m') {
      padding-right: 4vw;
    }

    @include productBreakpoint('desktop-l') {
      padding-right: 8vw;
    }
  }
}
</style>
